import { ChangeEvent, useCallback, useState, useEffect, useRef } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Card, Col, Form, Row, Modal } from 'react-bootstrap';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { InformationIcon } from '../../shared/InformationIcon';
import {
  getBooleanOptions,
  getDragClass,
  getDragContainerClass,
  getItemStyle,
  getOptions,
  getOptionsLabel,
  getOptionsString,
  getFilteredOptions,
} from '../common';
import { t } from '../../../services/translationService';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { setLoader } from '../../../redux/reducers/loaderSlice.reducer';
import { getAutoComplete } from '../../../services/autocompleteService';
import { formatToSwedishDecimal, parseSwedishDecimal } from '../../../services/editBookService';
import { Kapitel } from './Chapters';

interface Props {
  errors: any;
  register: any;
  control: any;
  getValues: any;
  setValue: any;
  trigger: any;
  blockRef: any;
    pageVariant: string;
    resetField: any;
}

export const General = ({
  errors,
  register,
  control,
  getValues,
  setValue,
  trigger,
  blockRef,
  pageVariant,
  resetField,
}: Props) => {
  const [miljomarkning, setMiljomarkning] = useState<string>('');
  const [batterityp, setBatterityp] = useState('');
    const [tryckortList, setTryckortList] = useState<any>([]);
    const [landList, setLandList] = useState<any>([]);
  const language = useAppSelector((state: any) => state.language.value);
    const [selectedTryckort, setSelectedTryckort] = useState<any>([]);
    const [selectedLand, setSelectedLand] = useState<any>([]);
    const [konsumentkontakt, setKonsumentkontakt] = useState<any>();
  const dispatch = useAppDispatch();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [showProductSafetyModal, setProductSafetyShowModal] = useState<boolean>(false);
    const [showConsumerContactModal, setShowConsumerContactModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(
    undefined
    );
    const [currentProductSafetyIndex, setCurrentProductSafetyIndex] = useState<number | undefined>(
        undefined
    );
    const [isNew, setIsNew] = useState<boolean>(false)
    const [originalProductSafetyValue, setOriginalProductSafetyValue] = useState<any>(undefined);

    const conformityDeclarationTypeValue = useRef("07");

  const {
    fields: miljomarkningar,
    append: appendMiljomarkning,
    move: moveMiljomarkning,
    remove: removeMiljomarkning,
  } = useFieldArray({
    control,
    name: 'miljomarkningar',
  });

  const {
    fields: batterityper,
    append: appendBatterityp,
    move: moveBatterityp,
    remove: removeBatterityp,
  } = useFieldArray({
    control,
    name: 'batterityp',
  });

  const {
    fields: innehall,
    append: appendInnehall,
    update: updateInnehall,
    move: moveInnehall,
    remove: removeInnehall,
  } = useFieldArray({
    control,
    name: 'innehall'
  });

    const {
        fields: produktsakerhetstyper,
        append: appendProduktakerhet,
        update: updateProduktsakerhet,
        remove: removeProduktakerhet,
    } = useFieldArray({
        control,
        name: 'produktsakerhet',
    });

    // Keep track of changes to Typ inside product safety modal.
    const productSafetyTypeWatch = useWatch({
        control,
        name: currentProductSafetyIndex != null ? `produktsakerhet.${currentProductSafetyIndex}.typ` : `produktsakerhet.${produktsakerhetstyper.length - 1}.typ`,
    });

  useEffect(() => {
      const properties = ["tryckort", "totalBatterivikt"]

    // Use Object.fromEntries to create an object from key-value pairs
    // Map over the properties array to create an array of [key, value] pairs
    const values = Object.fromEntries(properties.map((property, index) => [property, getValues(properties)[index]]));

    if (values.tryckort && values.tryckort[0]?.key) {
      setSelectedTryckort(values.tryckort);
    }

      setValue("totalBatterivikt", values.totalBatterivikt ? formatToSwedishDecimal(values.totalBatterivikt) : null)

      const land = getValues("konsumentkontakt.land");
      land && setSelectedLand([land]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTryckort && selectedTryckort[0]?.key) {
      setValue('tryckort', selectedTryckort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTryckort]);

    useEffect(() => {
        const currentKontakt = getValues('konsumentkontakt');
        if (selectedLand && selectedLand[0]?.key) {
            setValue('konsumentkontakt', { ...currentKontakt, land: selectedLand });
        } else {
            setValue('konsumentkontakt', { ...currentKontakt, land: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLand]);

    useEffect(() => {
        if (konsumentkontakt) {
            setValue('konsumentkontakt', konsumentkontakt);
        }
    }, [konsumentkontakt, setValue, getValues]);

  const displayModal = (index?: number) => {
    setIsNew(index == null);
    index === undefined ? appendInnehall({}) : setCurrentIndex(index);
    setShowModal(true);
    };

    // No value for index is a new entry. In case we are editing an already existing row, keep track of the old value
    // to enable resetting on exit.
    const displayProductSafetyModal = (index?: number) => {
        setIsNew(index == null);
        index === undefined ? appendProduktakerhet({}) : setCurrentProductSafetyIndex(index);
        setOriginalProductSafetyValue(structuredClone(getValues(index !== undefined ? `produktsakerhet.${index}` : `produktsakerhet.${produktsakerhetstyper.length - 1}`)));
        setProductSafetyShowModal(true);
    };

    // Changes made should be reset based on original values saved when opening the modal.
    const closeProductSafetyModal = () => {
        setProductSafetyShowModal(false);

        // New row created, remove it
        currentProductSafetyIndex === undefined && removeProduktakerhet(produktsakerhetstyper.length - 1);
        // Row edited, reset to it's previous values
        currentProductSafetyIndex !== undefined && !!originalProductSafetyValue && resetProductSafetyRow(currentProductSafetyIndex, originalProductSafetyValue);

        setCurrentProductSafetyIndex(undefined);
        setOriginalProductSafetyValue(undefined);
    };

    const displayConsumerContactModal = () => {
        setShowConsumerContactModal(true);
    };

    const closeModal = () => {
    setShowModal(false);
    currentIndex === undefined && removeInnehall(innehall.length - 1);
    };

    const closeConsumerContactModal = () => {
        setShowConsumerContactModal(false);
    };

    const saveConsumerContact = () => {
        trigger('konsumentkontakt').then((validResult: Boolean) => {
            if (validResult) {
                const changes = getValues(`konsumentkontakt`);
                setKonsumentkontakt(changes);
                setShowConsumerContactModal(false);
            }
        })
        
    }

    const saveProductSafety = () =>
        trigger(
            `produktsakerhet.${currentProductSafetyIndex !== undefined ? currentProductSafetyIndex : produktsakerhetstyper.length - 1
            }`
        ).then((validResult: boolean) => {
            if (validResult) {

                const changed = currentProductSafetyIndex === undefined
                    ? getValues(`produktsakerhet.${produktsakerhetstyper.length - 1}`)
                    : getValues(`produktsakerhet.${currentProductSafetyIndex}`);

                // No beskrivning saved unless correct value for Typ
                if (changed.typ !== conformityDeclarationTypeValue.current) {
                    changed.beskrivning = null;
                }

                currentProductSafetyIndex === undefined && removeProduktakerhet(produktsakerhetstyper.length - 1);

                currentProductSafetyIndex !== undefined
                    ? updateProduktsakerhet(
                        currentProductSafetyIndex,
                        {
                            ...changed
                        }
                    )
                    : appendProduktakerhet({
                        ...changed
                    });

                setProductSafetyShowModal(false);
            }
        });

    // Cannot safely be done by resetting the entire object.
    const resetProductSafetyRow = (index: number, value: any) => {
        resetField(`produktsakerhet.${index}.typ`, {
            defaultValue: value.typ,
        });
        resetField(`produktsakerhet.${index}.beskrivning`, {
            defaultValue: value.beskrivning,
        });
    };

  const saveInnehall = () =>
    trigger(
      `innehall.${currentIndex !== undefined ? currentIndex : innehall.length - 1
      }`
    ).then((validResult: boolean) => {
      if (validResult) {

        const changed = currentIndex === undefined
          ? getValues(`innehall.${innehall.length - 1}`)
          : getValues(`innehall.${currentIndex}`);

        currentIndex === undefined && removeInnehall(innehall.length - 1);
        if (changed.primary) {
          let allInnehall = getValues('innehall');
          allInnehall.forEach((item: any) => {
            if (item.key !== changed.key) {
              item.primary = false;
            }
          });
        }

        currentIndex !== undefined
          ? updateInnehall(
            currentIndex,
            {
              ...changed
            }
          )
          : appendInnehall({
            ...changed
          });

        setShowModal(false);
      }
    });

  const onInnehallDragEnd = (result: any) => {
    moveInnehall(result.source.index, result.destination.index);
  }

  const addMiljomarkning = () => {
    if (miljomarkning !== '') {
      appendMiljomarkning(miljomarkning);
      setMiljomarkning('');
    }
  };

  const updateMiljomarkning = (event: ChangeEvent<HTMLInputElement>): void => {
    let value: string = (event.target as HTMLInputElement).value;
    setMiljomarkning(value);
  };

  const onMiljomarkningDragEnd = (result: any) => {
    moveMiljomarkning(result.source.index, result.destination.index);
  };

  const addBatterityp = () => {
    if (batterityp) {
      appendBatterityp(batterityp);
      setBatterityp('');
    }
  };

  const updateBatterityp = (event: ChangeEvent<HTMLInputElement>): void => {
    let value: string = (event.target as HTMLInputElement).value;
    setBatterityp(value);
  };

  const onBatteritypDragEnd = (result: any) => {
    moveBatterityp(result.source.index, result.destination.index);
    };

  const getSuggestions = useCallback(
      (queryString: string, type: string): void => {
      dispatch(setLoader(true));
      getAutoComplete(queryString, type, language)
        .then((data: any) => {
          setTryckortList(data);
          dispatch(setLoader(false));
        })
        .catch((err) => console.log(err));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
    );

    const getCountrySuggestions = useCallback(
        (queryString: string, type: string): void => {
            dispatch(setLoader(true));
            getAutoComplete(queryString, type, language)
                .then((data: any) => {
                    setLandList(data);
                    dispatch(setLoader(false));
                })
                .catch((err) => console.log(err));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

  return (
    <>
      <h3 ref={blockRef}>{t('ovrigInformation')}</h3>
      <Col xs={12}>
        <hr className="u-text-grey" />
      </Col>
      <Card className="nopadding my-4">
        <Card.Body className="p-3">
          <Row>
            {/*Tryckort*/}
            <Col xs={7}>
              <Form.Group as={Col} className="mb-3 col me-3">
                <Form.Label className="mb-1 text-body">
                    {t('tryckort')}
                    <InformationIcon
                        text={t('hjalpTryckort')}
                        inline={true}
                        className="ps-1"
                    />
                </Form.Label>
                <Controller
                  control={control}
                  name="tryckort"
                  render={({ field, fieldState }) => (
                    <AsyncTypeahead
                      {...field}
                      selected={selectedTryckort}
                      id="tryckort"
                      labelKey={'value'}
                      isLoading={false}
                      options={tryckortList}
                      placeholder={t('skrivEllips')}
                      inputProps={{
                        className: `${errors.tryckort ? 'is-invalid' : ''}`,
                      }}
                      filterBy={() => true}
                      onSearch={(query: string) => {
                        getSuggestions(query, 'Countries');
                      }}
                      onChange={(selected: any) => {
                        setSelectedTryckort(selected)
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            {/*Miljömärkning*/}
            <Col xs={12}>
              <div className="d-flex align-items-end w-100">
                <Form.Group as={Col} className="mb-3 col me-3">
                  <Form.Label className="mb-1 text-body">
                    {t('miljomarkning')}
                    <InformationIcon
                        text={t('hjalpMiljomarkning')}
                        inline={true}
                        className="ps-1"
                    />
                  </Form.Label>
                  <Form.Control
                    as={'select'}
                    type="select"
                    className="form-select"
                    placeholder={t('valjEllips')}
                    value={miljomarkning}
                    onChange={updateMiljomarkning}
                  >
                    {getOptionsString('environmentLabels')}
                  </Form.Control>
                </Form.Group>
                <Form.Group as={Col} className="mb-3 col-auto">
                  <Button
                    className="primary"
                    type="button"
                    onClick={addMiljomarkning}
                  >
                    <i className="bi bi-plus"></i>
                  </Button>
                </Form.Group>
              </div>
            </Col>
            <DragDropContext onDragEnd={onMiljomarkningDragEnd}>
              <Droppable droppableId="droppablemiljomarkning">
                {(provided: any, snapshot: any) => (
                  <Col
                    xs={12}
                    className={`mb-3 ${getDragContainerClass(
                      snapshot.isDraggingOver
                    )}`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {miljomarkningar.map((row: any, index: number) => {
                      return (
                        <Draggable
                          key={row.id}
                          draggableId={row.id}
                          index={index}
                        >
                          {(provided: any, snapshot: any) => (
                            <Row
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                              key={row.id}
                              className={`${index % 2 === 1 ? 'u-background-grey' : ''
                                } mt-2 p-1 ${getDragClass(snapshot.isDragging)}`}
                            >
                              <Col
                                xs={11}
                                className="d-flex align-items-center"
                              >
                                {getValues(`miljomarkningar.${index}`)}
                              </Col>
                              <Col xs={1} className="d-flex align-items-center">
                                <i
                                  className="bi bi-trash3 u-text-error u-cursor-pointer mx-auto"
                                  onClick={() => removeMiljomarkning(index)}
                                />
                              </Col>
                            </Row>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Col>
                )}
              </Droppable>
            </DragDropContext>
            <Col xs={12}>
              <hr className="u-text-grey" />
            </Col>
            <Col xs={12}>
              <Row>
                {pageVariant === 'large' && (
                  /*CE-märkning*/
                  <Col xs={6}>
                    <Form.Group as={Col} className="mb-3 col me-3">
                      <Form.Label className="mb-1 text-body">
                        {t('ceMarkning')}
                      </Form.Label>
                      <Form.Control
                        as={'select'}
                        type="select"
                        className="form-select"
                        {...register('ceMarkning', {
                          setValueAs: (v: any) => (v === 'true' || v === true)
                        })}
                      >
                        {getBooleanOptions()}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                )}
                <Col xs={pageVariant === 'large' ? 7 : 12}>
                  <Form.Group className="mb-3">
                    {/*Prisgrupp*/}
                    <Form.Label className="mb-1 text-body">
                      {t('prisgrupp')}
                      <InformationIcon
                        text={t('hjalpPrisgrupp')}
                        inline
                        className="ps-1"
                      />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('prisgrupp')}
                    />
                  </Form.Group>
                  {/*Senaste trycknummer*/}
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                      {t('senasteTryckNummer')}
                      <InformationIcon
                        text={t('hjalpSenasteTryckNummer')}
                        inline
                        className="ps-1"
                      />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('senasteTryckNummer', {
                        setValueAs: (value: any) => !!value ? value : null
                      })}
                    />
                  </Form.Group>
                  {/*Tullkod*/}
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t('tullkod')}
                        <InformationIcon
                            text={t('hjalpTullkod')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('tullkod')}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <hr className="u-text-grey" />
                </Col>
                {/*Total batterivikt*/}
                <Col xs={7}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                      {t('totalBatterivikt')}
                      <InformationIcon
                        text={t('hjalpBatterivikt')}
                        inline
                        className="ps-1"
                      />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('totalBatterivikt', {
                        setValueAs: (value: any) => {
                          if (!value) return null; // Tillåt tomma värden
                          try {
                            return parseSwedishDecimal(value); // Försök att parsa värdet
                          } catch (error: any) {
                            return error; // Returnera felmeddelandet om parsing misslyckas
                          }
                        },
                        validate: (value: any) => {
                          return !isNaN(Number(value)) || `${t('totalBatterivikt')} ${t('isNotAValidNumber') || value}`
                        },
                      })}
                    />
                    {errors.totalBatterivikt && (
                      <Form.Text className="text-danger">
                        {errors.totalBatterivikt.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                {/*Batterityp*/}
                <Col xs={12}>
                  <div className="d-flex align-items-end w-100">
                    <Form.Group as={Col} className="mb-3 col me-3">
                      <Form.Label className="mb-1 text-body">
                        {t("batteriTyp")}
                        <InformationIcon
                          text={t('hjalpBatteriTyp')}
                          inline
                          className="ps-1"
                        />
                      </Form.Label>
                      <Form.Control
                        as={'select'}
                        type="select"
                        className="form-select"
                        placeholder={t('valjEllips')}
                        value={batterityp}
                        onChange={updateBatterityp}
                      >
                        {getFilteredOptions('batteryTypes', getValues('batterityp'))}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group as={Col} className="mb-3 col-auto">
                      <Button
                        className="primary"
                        type="button"
                        onClick={addBatterityp}
                      >
                        <i className="bi bi-plus"></i>
                      </Button>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
            </Col>
            <DragDropContext onDragEnd={onBatteritypDragEnd}>
              <Droppable droppableId="droppablebatterityp">
                {(provided: any, snapshot: any) => (
                  <Col
                    xs={12}
                    className={`mb-3 ${getDragContainerClass(
                      snapshot.isDraggingOver
                    )}`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {batterityper.map((row: any, index: number) => {
                      return (
                        <Draggable
                          key={row.id}
                          draggableId={row.id}
                          index={index}
                        >
                          {(provided: any, snapshot: any) => (
                            <Row
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                              key={row.id}
                              className={`${index % 2 === 1 ? 'u-background-grey' : ''
                                } mt-2 p-1 ${getDragClass(snapshot.isDragging)}`}
                            >
                              <Col
                                xs={11}
                                className="d-flex align-items-center"
                              >
                                {getOptionsLabel("batteryTypes", getValues(`batterityp.${index}`))}
                              </Col>
                              <Col xs={1} className="d-flex align-items-center">
                                <i
                                  className="bi bi-trash3 u-text-error u-cursor-pointer mx-auto"
                                  onClick={() => removeBatterityp(index)}
                                />
                              </Col>
                            </Row>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Col>
                )}
              </Droppable>
            </DragDropContext>
            {/*Batteribeskrivning*/}
            <Form.Group className="mb-3">
              <Form.Label className="mb-1 text-body">
                {t('batteriBeskrivning')}
              </Form.Label>
              <Form.Control
                placeholder={t('skrivEllips')}
                {...register('batteriBeskrivning')}
              />
            </Form.Group>
            <Col xs={12}>
              <hr className="u-text-grey" />
            </Col>
            {/*Innehåll*/}
            <Col xs={12}>
              <Form.Label className="mb-1 text-body">
                {t('innehall')}
                <InformationIcon
                    text={t('hjalpInnehall')}
                    inline={true}
                    className="ps-1"
                />
              </Form.Label>
              <Card className="mb-4 nopadding">
                <Card.Body>
                  {innehall?.length > 0 && (
                    <div className='mb-3 w-100 table'>
                      <div className="small px-3 py-1 d-flex border-bottom">
                        <div style={{ width: "10%" }}>
                          <span className="d-block">{t("primar")}</span>
                        </div>
                        <div style={{ width: "20%" }}>
                          <span className="d-block">{"Kod"}</span>
                        </div>
                        <div style={{ width: "50%" }}>
                          <span className="d-block">{t("beskrivning")}</span>
                        </div>
                        <div style={{ width: "20%" }}>
                          <span className="d-block"></span>
                        </div>
                      </div>
                      <DragDropContext onDragEnd={onInnehallDragEnd}>
                        <Droppable droppableId="droppableInnehall">
                          {(provided: any, snapshot: any) => (
                            <div
                              className={`${getDragContainerClass(
                                snapshot.isDraggingOver
                              )}`}
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >

                              {innehall.map((row: any, index: number) => {
                                return (
                                  <Draggable
                                    key={row.id}
                                    draggableId={row.id}
                                    index={index}
                                    isDragDisabled={true}
                                  >
                                    {(provided: any, snapshot: any) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={getItemStyle(
                                          snapshot.isDragging,
                                          provided.draggableProps.style
                                        )}
                                        key={row.id}
                                        className={`${index % 2 === 1 ? 'u-background-grey' : ''
                                          } d-flex gx-0 px-3 u-text-small-medium u-text-grey py-3 align-items-center border-bottom ${getDragClass(
                                            snapshot.isDragging
                                          )}`}
                                      >
                                        <div style={{ width: "10%" }}>
                                          {row.primary && <i className="bi bi-check" />}
                                        </div>
                                        <div style={{ width: "20%" }}>
                                          {row.key}
                                        </div>
                                        <div style={{ width: "50%" }}>
                                          {getOptionsLabel("productContentCategories", row.key)}
                                        </div>
                                        <div style={{ width: "20%" }}>
                                          <div className="d-flex gap-3 justify-content-end">
                                            <i
                                              className="bi bi-pencil u-text-primary u-cursor-pointer"
                                              onClick={() => displayModal(index)}
                                            />
                                            <i
                                              className="bi bi-trash3 u-text-error u-cursor-pointer"
                                              onClick={() => removeInnehall(index)}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer>
                  <Button
                    type="button"
                    className="primary"
                    onClick={() => displayModal()}
                  >
                    <i className="bi bi-plus" />
                    {t("laggTillInnehall")}
                  </Button>
                </Card.Footer>
              </Card>
              <Modal
                show={showModal}
                centered
                size="lg"
                onHide={closeModal}
                onExited={() => setCurrentIndex(undefined)}
              >
                <Modal.Header>
                  <p>{t('laggTillInnehall')}</p>
                </Modal.Header>
                <Modal.Body>
                  {innehall.map((row: any, index: number) => {
                    return (
                      index ===
                      (currentIndex !== undefined
                        ? currentIndex
                        : innehall.length - 1) && (
                        <Row key={row.id}>
                          <Col xs={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="mb-1 text-body">
                                {t("primar")}
                              </Form.Label>
                              <Form.Control
                                as={'select'}
                                type="select"
                                className="form-select"
                                placeholder={t('valjEllips')}
                                {...register(`innehall.${index}.primary`, {
                                  setValueAs: (v: any) => (v === 'true' || v === true),
                                })
                                }
                                isInvalid={
                                  !!errors.innehall && errors.innehall[index].primary
                                }
                              >
                                {getBooleanOptions()}
                              </Form.Control>
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="mb-1 text-body">
                                {t("innehall")}
                              </Form.Label>
                              <Form.Control
                                as={'select'}
                                type="select"
                                className="form-select"
                                placeholder={t('valjEllips')}
                                {...register(`innehall.${index}.key`, { required: "" })}
                                isInvalid={
                                  !!errors.innehall && errors.innehall[index].key
                                }
                              >
                                {getOptions("productContentCategories")}
                              </Form.Control>
                            </Form.Group>
                          </Col>
                        </Row>
                      )
                    );
                  })}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={closeModal}>
                    {t('avbryt')}
                  </Button>
                  <Button variant="primary" onClick={saveInnehall}>
                    {isNew ? t('laggTill') : t('uppdatera')}
                  </Button>
                </Modal.Footer>
              </Modal>
                      </Col>
            </Row>
            <Kapitel
              errors={errors}
              register={register}
              control={control}
              getValues={getValues}
              setValue={setValue}
              trigger={trigger}
            />
                      {/* Produktsäkerhet */}
                  <Col xs={12}>
                      <Form.Label className="mb-1 text-body">
                          {t("produktsakerhet")}
                      </Form.Label>
                      <Card className="mb-4 nopadding">
                          <Card.Body className="">
                              {produktsakerhetstyper.map((row: any, index: number) => {
                                  return <Row key={row.id} className={`${index % 2 === 1 ? 'u-background-grey' : ''} mt-2 p-1`}>
                                      <Col xs={4}>
                                          {getOptionsLabel("productSafetyTypes", getValues(`produktsakerhet.${index}.typ`))}
                                      </Col>
                                      <Col xs={7}>
                                          {getValues(`produktsakerhet.${index}.beskrivning`)}
                                      </Col>
                                      <Col xs={1} className="d-flex align-items-center">
                                          <i
                                              className="bi bi-pencil u-text-primary u-cursor-pointer"
                                              onClick={() => displayProductSafetyModal(index)}
                                          />
                                          <i
                                              className="bi bi-trash3 u-text-error u-cursor-pointer mx-auto"
                                              onClick={() => removeProduktakerhet(index)}
                                          />
                                      </Col>
                                  </Row>
                                  
                              })}
                          </Card.Body>
                          <Card.Footer>
                          
                              <Button
                                  type="button"
                                  className="primary"
                                  onClick={() => displayProductSafetyModal()}
                              >
                                  <i className="bi bi-plus" />
                                  {t("redigera")}
                              </Button>
                          </Card.Footer>
                      </Card>
                  </Col>
                          <Modal
                              show={showProductSafetyModal}
                              centered
                              size="lg"
                              onHide={closeModal}
                              onExited={() => setCurrentProductSafetyIndex(undefined)}
                          >
                              <Modal.Header>
                                  <p>{t("produktsakerhet")}</p>
                              </Modal.Header>
                              <Modal.Body>
                          {produktsakerhetstyper.map((row: any, index: number) => {
                                      return (
                                          index ===
                                          (currentProductSafetyIndex !== undefined
                                              ? currentProductSafetyIndex
                                              : produktsakerhetstyper.length - 1) && (
                                              <Row key={row.id} className="mt-2">
                                                  <Col xs={12}>
                                                      <Form.Group className="mb-3">
                                                          <Form.Label className="mb-1 text-body">
                                                              {t("typ")}
                                                        </Form.Label>
                                                          <Form.Control
                                                              as={'select'}
                                                              type="select"
                                                              className="form-select"
                                                              placeholder={t('valjEllips')}
                                                              {...register(`produktsakerhet.${index}.typ`, { required: "Typ är obligatorisk" })}
                                                              isInvalid={
                                                                  !!errors.produktsakerhet && errors.produktsakerhet[index].typ
                                                              }
                                                          >
                                                              {getOptions("productSafetyTypes")}
                                                          </Form.Control>
                                                      </Form.Group>
                                                  </Col>
                                                  {productSafetyTypeWatch === conformityDeclarationTypeValue.current &&
                                                      <Col xs={12}>
                                                          <Form.Group className="mb-3">
                                                              <Form.Label className="mb-1 text-body">
                                                                  {t("konformitetsdeklaration")}
                                                                  <InformationIcon
                                                                      text={t('hjalpKonformitetsdeklaration')}
                                                                      inline
                                                                      className="ps-1"
                                                                  />
                                                              </Form.Label>
                                                              <Form.Control
                                                                  placeholder={t('skrivEllips')}
                                                                  {...register(`produktsakerhet.${index}.beskrivning`, {
                                                                      validate: (value: any) => {
                                                                          const typ = getValues(`produktsakerhet.${index}.typ`);
                                                                          if (typ === conformityDeclarationTypeValue.current && !value) {
                                                                              return "Konformitetsdeklaration krävs";
                                                                          }
                                                                          return true;
                                                                      }
                                                                  })}
                                                                  isInvalid={
                                                                      !!errors.produktsakerhet && errors.produktsakerhet[index].beskrivning
                                                                  }
                                                              />
                                                              {errors.produktsakerhet && errors.produktsakerhet[index].beskrivning && (
                                                                  <Form.Control.Feedback type="invalid">
                                                                      {`${errors.produktsakerhet[index].beskrivning.message}`}
                                                                  </Form.Control.Feedback>
                                                              )}
                                                          </Form.Group>
                                                      </Col>
                                                  }
                                              </Row>
                                          )
                                      );
                                  })}
                              </Modal.Body>
                              <Modal.Footer>
                                  <Button variant="secondary" onClick={closeProductSafetyModal}>
                                      {t('avbryt')}
                                  </Button>
                                  <Button variant="primary" onClick={saveProductSafety}>
                                      {isNew ? t('laggTill') : t('uppdatera')}
                                  </Button>
                              </Modal.Footer>
                          </Modal>
                  {/* Konsumentkontakt */}
                  <Col xs={12}>
                      <Form.Label className="mb-2 text-body">
                          {t("konsumentkontaktTitel")}
                      </Form.Label>
                      <Card className="mb-4 nopadding">
                          <Card.Body>
                                <div className='mb-3 w-100 row mt-2 p-1'>
                                  {getValues("konsumentkontakt.foretag") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktForetag")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.foretag")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.kontakt") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktKontakt")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.kontakt")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.telefonnummer") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktTelefonnummer")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.telefonnummer")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.epostadress") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktEpostadress")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.epostadress")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.gatuadress") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktGatuadress")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.gatuadress")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.ort") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktOrt")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.ort")}</Col>
                                      </>
                                  )}

                                  {getValues("konsumentkontakt.postnummer") && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktPostnummer")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{getValues("konsumentkontakt.postnummer")}</Col>
                                      </>
                                  )}

                                  {selectedLand?.length > 0 && (
                                      <>
                                          <Col xs={4}>
                                              <Form.Label className="mb-1 text-body">{t("konsumentkontaktLand")}</Form.Label>
                                          </Col>
                                          <Col xs={8}>{selectedLand[0]?.value}</Col>
                                      </>
                                  )}

                                </div>
                          </Card.Body>
                          <Card.Footer>
                              <Button
                                  type="button"
                                  className="primary"
                                  onClick={() => displayConsumerContactModal()}
                              >
                                  <i className="bi bi-plus" />
                                  {t("redigera")}
                              </Button>
                          </Card.Footer>
                      </Card>
                      <Modal
                          show={showConsumerContactModal}
                          centered
                          size="lg"
                          onHide={closeConsumerContactModal}
                          onExited={() => setCurrentIndex(undefined)}
                      >
                          <Modal.Header>
                              <p>{t("konsumentkontaktTitel")}</p>
                          </Modal.Header>
                          <Modal.Body>
                               <Row>
                                      <Col xs={12}>
                                          <Form.Group className="mb-3">
                                              <Form.Label className="mb-1 text-body">
                                              {t("konsumentkontaktForetag")}
                                              <InformationIcon
                                                  text={t('hjalpKonsumentkontaktForetag')}
                                                  inline
                                                  className="ps-1"
                                              />
                                              </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.foretag')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktKontakt")}
                                              <InformationIcon
                                                  text={t('hjalpKonsumentkontaktKontakt')}
                                                  inline
                                                  className="ps-1"
                                              />
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.kontakt')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktTelefonnummer")}
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.telefonnummer')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktEpostadress")}
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.epostadress', {
                                                  pattern: {
                                                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: t("valideringEpost") }
                                              })}
                                              isInvalid={
                                                  !!errors.konsumentkontakt && errors.konsumentkontakt?.epostadress
                                              }
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktGatuadress")}
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.gatuadress')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktOrt")}
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.ort')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktPostnummer")}
                                          </Form.Label>
                                          <Form.Control
                                              placeholder={t('skrivEllips')}
                                              {...register('konsumentkontakt.postnummer')}
                                          />
                                          <Form.Label className="mb-1 mt-2 text-body">
                                              {t("konsumentkontaktLand")}
                                          </Form.Label>
                                          <Controller
                                              control={control}
                                              name="land"
                                              render={({ field, fieldState }) => (
                                                  <AsyncTypeahead
                                                      {...field}
                                                      selected={selectedLand ?? []}
                                                      id="land"
                                                      labelKey={'value'}
                                                      isLoading={false}
                                                      options={landList}
                                                      placeholder={t('skrivEllips')}
                                                      inputProps={{
                                                          className: `${errors.konsumentkontakt?.land ? 'is-invalid' : ''}`,
                                                      }}
                                                      filterBy={() => true}
                                                      onSearch={(query: string) => {
                                                          getCountrySuggestions(query, 'Countries');
                                                      }}
                                                      onChange={(selected: any) => {
                                                          setSelectedLand(selected)
                                                      }}
                                                  />
                                              )}
                                          />
                                          </Form.Group>
                                      </Col>
                                  </Row>
                                
                          </Modal.Body>
                          <Modal.Footer>
                              <Button variant="secondary" onClick={closeConsumerContactModal}>
                                  {t('avbryt')}
                              </Button>
                              <Button variant="primary" onClick={saveConsumerContact}>
                                  {t('spara')}
                              </Button>
                          </Modal.Footer>
                      </Modal>
                  </Col>
        </Card.Body>
      </Card>
    </>
  );
};
